import { contractRequest } from "@/portals/clinician/api/transport";
import type { ReviewTriggerType } from "@/features/patient/records/api/dataPipelineApi";
import type { AttachedVia, ClinicianItemStatus, QueueItemPreview } from "@/portals/clinician/api/reviewQueueApi";

// Module 6 — admin oversight of the shared review queue. Admin token; failures go
// through the one contract error parser (contractErrors.ts).

export interface ReviewQueueSummary {
  /** AWAITING_CLINICIAN with no active holder. */
  unclaimedCount: number;
  /** Of those, waiting longer than UNCLAIMED_ALERT_HOURS. */
  overdueCount: number;
  oldestWaitingSince: string | null;
}

export interface AdminQueueItem {
  id: string;
  triggerType: ReviewTriggerType;
  /** null on a GOLDEN_RECORD_CONFLICT about a condition, allergy or lab result. */
  medicineName: string | null;
  patientDisplayName: string;
  waitingSince: string;
  overdue: boolean;
}

export type AdminItemState = "IN_QUEUE" | "WITH_CLINICIAN" | "CLOSED";

export interface AdminItemClinician {
  userId: string;
  name: string;
  email: string;
}

/** Read-only view of any task (queue View, Assigned Items): what's inside it,
 *  the patient (admins may see the name), and who holds it now (or last held it). */
export interface AdminQueueItemDetail extends QueueItemPreview {
  patient: { displayName: string; dateOfBirth: string | null };
  state?: AdminItemState;
  clinician?: AdminItemClinician | null;
  attachedVia?: AttachedVia | null;
  attachedAt?: string | null;
  resolvedAt?: string | null;
}

/** A row of the Assigned Items page. */
export interface AdminAssignedItem {
  id: string;
  triggerType: ReviewTriggerType;
  itemStatus: ClinicianItemStatus;
  state: AdminItemState;
  medicineName: string | null;
  patientDisplayName: string;
  clinician: AdminItemClinician | null;
  attachedVia: AttachedVia | null;
  assignedByAdmin: string | null;
  attachedAt: string | null;
  lastActivityAt: string;
  awaitingPatientReply: boolean;
  resolvedAt: string | null;
  /** The clinician's decision (DecisionAction) or "PATIENT_NO_LONGER_TAKES"; null while open. */
  outcome: string | null;
  createdAt: string;
}

export interface AssignResult {
  id: string;
  itemStatus: "IN_CLINICIAN_REVIEW";
  clinician: { userId: string; firstName: string; lastName: string };
}

export const getReviewQueueSummaryApi = () =>
  contractRequest<ReviewQueueSummary>("/api/v1/admin/review-queue/summary", { method: "GET", portal: "admin" });

/** Oldest first. */
export const listAdminReviewQueueApi = () =>
  contractRequest<{ items: AdminQueueItem[] }>("/api/v1/admin/review-queue", { method: "GET", portal: "admin" });

/** Read-only, changes nothing — any item. 200 · 404 REVIEW_ITEM_NOT_FOUND */
export const getAdminQueueItemApi = (id: string) =>
  contractRequest<AdminQueueItemDetail>(`/api/v1/admin/review-queue/${encodeURIComponent(id)}`, { method: "GET", portal: "admin" });

/** 404 REVIEW_ITEM_NOT_FOUND / CLINICIAN_NOT_FOUND · 409 ITEM_NOT_ASSIGNABLE / CLINICIAN_NOT_ELIGIBLE */
export const assignReviewItemApi = (id: string, clinicianUserId: string) =>
  contractRequest<AssignResult>(`/api/v1/admin/review-items/${encodeURIComponent(id)}/assign`, {
    method: "POST",
    portal: "admin",
    body: { clinicianUserId },
  });

/** Every item that left the patient's hands — who holds it, since when, its state. */
export const listAssignedItemsApi = (filters: { state?: AdminItemState; clinicianUserId?: string } = {}) => {
  const params = new URLSearchParams();
  if (filters.state) params.set("state", filters.state);
  if (filters.clinicianUserId) params.set("clinicianUserId", filters.clinicianUserId);
  const qs = params.toString();
  return contractRequest<{ items: AdminAssignedItem[] }>(`/api/v1/admin/assigned-items${qs ? `?${qs}` : ""}`, {
    method: "GET",
    portal: "admin",
  });
};

/** Takes a held item back into the shared queue. 200 · 409 ITEM_NOT_HELD · 404 REVIEW_ITEM_NOT_FOUND */
export const returnItemToQueueApi = (id: string) =>
  contractRequest<{ id: string; itemStatus: "AWAITING_CLINICIAN" }>(
    `/api/v1/admin/review-items/${encodeURIComponent(id)}/return-to-queue`,
    { method: "POST", portal: "admin" },
  );
